import { ChannelState } from '../../../enums/channel-state.enum';
import { DateFormats } from '../../../enums/date-formats';
import { generateStepName } from '../../../pages/Recipes/utils';
import { IPostWeldRecipeStep } from '../../../types/cycle-settings-types';
import { DateFormatter } from '../../../utils/date-formatter';
import { convertPostWeldStepToEditable } from '../utils';
import { EditableStepsTable } from './EditableStepsTable';
import { TableState } from './TableState';
import CycleMeasurement from '../../../enums/cycle-measurement';
import { convertAbsoluteTemperature, convertTemperatureDelta } from '../../../utils/temperature-unit-conversion';
import { StepMode } from '../../../types/recipes-types';

export interface StepsTableProps {
  currentStepIdx: number;
  stepMinutesLeft: number;
  channelState: ChannelState;
  deviceSteps: IPostWeldRecipeStep[];
  jumpPrevStep: () => any;
  jumpNextStep: () => any;
  measurement: CycleMeasurement;
}

const generatePostWeldStepName = (item: IPostWeldRecipeStep, measurement: CycleMeasurement) => {
  const editable = convertPostWeldStepToEditable(item);

  if (editable.mode === StepMode.Rate) {
    const sourceMeasurement = editable.isCelsius ? CycleMeasurement.Celsius : CycleMeasurement.Fahrenheit;

    return generateStepName({
      ...editable,
      heatingRate: convertTemperatureDelta(editable.heatingRate, sourceMeasurement, measurement) as number,
      heatingLimit: convertAbsoluteTemperature(editable.heatingLimit, sourceMeasurement, measurement) as number,
      isCelsius: measurement === CycleMeasurement.Celsius,
      units: measurement,
    });
  }

  return generateStepName(editable);
};

export const DeviceStepsTable = ({
  deviceSteps,
  currentStepIdx,
  stepMinutesLeft,
  channelState,
  jumpPrevStep,
  jumpNextStep,
  measurement,
}: StepsTableProps) => {
  const currentValues =
    deviceSteps.map((item, idx) => ({
      Name: `${idx + 1}. ${generatePostWeldStepName(item, measurement)}`,
      id: `${idx}`,
      idx,
      State:
        channelState !== ChannelState.STOPPED ? (
          <TableState
            disablePrev={currentStepIdx <= 1}
            disableNext={currentStepIdx >= deviceSteps.length}
            active={currentStepIdx === item.order.value + 1}
            finished={currentStepIdx > item.order.value + 1}
            onPressNext={jumpNextStep}
            onPressPrev={jumpPrevStep}
            currText={`${stepMinutesLeft} minutes left`}
          />
        ) : (
          ''
        ),
      Date: (
        <div className="flex w-[180px] items-center justify-center">
          <p>{DateFormatter({ date: new Date(item.mode.timestamp), format: DateFormats['dd-MMM yyyy hh:mm:ss a'] })}</p>
        </div>
      ),
      order: item.order.value,
    })) || [];

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex h-10 w-full flex-row items-center justify-between">
        <h2 className="text-title-md2 font-semibold text-black dark:text-white">Device Steps</h2>
      </div>
      <div>
        <EditableStepsTable values={currentValues} defaultKeys={['Name', 'State', 'Date']} />
      </div>
    </div>
  );
};
